import { ref, computed, watch, provide, inject, type Ref, onMounted, nextTick } from 'vue'
import type { FormState } from '@/types'
import { useQuestionnaireStore, type SectionInfo } from '@/store/questionnaireStore.ts'
import { getComponent } from '../components/questionaire/componentTypesV3'
import {
  useFieldValidation as useValidationService,
  type FieldValidation
} from '@/services/validation.service.ts'
import {
  QuestionnaireSubmissionsService,
  type QuestionnaireSection,
  type QuestionnaireQuestion
} from '@/types/generated'
import { toast } from '@/components/ui/toast'
import { v4 as uuidv4 } from 'uuid'

const fieldValidationErrors = ref<Record<string, boolean>>({})

// Per-submission save queues to serialize concurrent save operations
const submissionSaveQueues = new WeakMap<object, Promise<boolean>>()

function getSaveQueue(submission: object): Promise<boolean> {
  return submissionSaveQueues.get(submission) ?? Promise.resolve(true)
}

function setSaveQueue(submission: object, queue: Promise<boolean>): void {
  submissionSaveQueues.set(submission, queue)
}

function patchFieldData(uuid: string, value: any, submission: any) {
  const fieldHasErrors = fieldValidationErrors.value[uuid] || false
  if (fieldHasErrors) {
    return false
  }

  if (value === undefined || value === null || value === '') {
    return false
  }

  let updatedSchema: any[] | undefined
  if (submission?.schema && Array.isArray(submission.schema)) {
    updatedSchema = submission.schema.map((page: any) => ({
      ...page,
      items: page.items.map((item: any) => {
        if (item.uuid === uuid) {
          return { ...item, value: value }
        }
        return item
      })
    }))
  }

  return updatedSchema
}

function updateFieldValidationError(fieldId: string, hasError: boolean) {
  fieldValidationErrors.value[fieldId] = hasError
}

async function saveFieldData(uuid: string, value: any, submission: any) {
  if (!submission || typeof submission !== 'object') {
    return false
  }

  // Queue saves to prevent race conditions when user navigates quickly
  const saveOperation = async (): Promise<boolean> => {
    // Patch uses current submission.schema which may have been updated by previous queued saves
    const updatedSchema = patchFieldData(uuid, value, submission)
    if (!updatedSchema) {
      return false
    }

    // Update local schema immediately (optimistically) before API call
    // This ensures subsequent queued saves see the updated value
    if (submission.schema) {
      submission.schema = updatedSchema
    }

    try {
      await QuestionnaireSubmissionsService.questionnaireSubmissionsPartialUpdate({
        uuid: submission.uuid || '',
        requestBody: {
          schema: updatedSchema,
          closed: false
        }
      })

      return true
    } catch (error) {
      console.error('Error updating submission:', error)
      toast({
        title: 'Oh, oh!',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      })
      return false
    }
  }

  // Chain onto the submission-specific queue so saves execute sequentially per submission
  const currentQueue = getSaveQueue(submission)
  const newQueue = currentQueue.then(saveOperation, saveOperation)
  setSaveQueue(submission, newQueue)
  return newQueue
}

export function provideQuestionnaire() {
  provide('fieldValidationErrors', fieldValidationErrors)
  provide('patchFieldData', patchFieldData)
  provide('updateFieldValidationError', updateFieldValidationError)
  provide('saveFieldData', saveFieldData)

  return {
    fieldValidationErrors,
    patchFieldData,
    updateFieldValidationError,
    saveFieldData
  }
}

export function useQuestionnaire() {
  const fieldValidationErrors = inject<Ref<Record<string, boolean>>>(
    'fieldValidationErrors',
    ref({})
  )
  const patchFieldData = inject<Function>('patchFieldData', () => false)
  const updateFieldValidationError = inject<Function>('updateFieldValidationError', () => {})
  const saveFieldData = inject<Function>('saveFieldData', () => false)

  return {
    fieldValidationErrors,
    patchFieldData,
    updateFieldValidationError,
    saveFieldData
  }
}

export function useQuestionnaireForm(rawSchema: QuestionnaireSection[]) {
  const questionnaireStore = useQuestionnaireStore()

  questionnaireStore.initializeForm(rawSchema)

  const { fieldValidationErrors, updateFieldValidationError, saveFieldData } = useQuestionnaire()

  // Use store's state for repeat section prompt (so sidebar can also reset it)
  const showRepeatSectionPrompt = computed(() => questionnaireStore.showRepeatSectionPrompt)
  const pendingRepeatSectionInfo = computed(() => questionnaireStore.pendingRepeatSectionInfo)

  const fieldsWithComponents = computed(() => {
    return questionnaireStore.flatFields.map((field) => ({
      ...field,
      component: getComponent(field.type)
    }))
  })

  const formState = ref<FormState>({
    activeFieldId: questionnaireStore.activeFieldId,
    activeFieldIndex: questionnaireStore.activeFieldIndex,
    valid: true,
    next: true,
    formLength: questionnaireStore.formLength,
    isLastField: questionnaireStore.isLastField,
    isFirstField: computed(() => questionnaireStore.isFirstField),
    errorLength: 0
  })

  // Watch for store changes and update local state
  const updateFormState = () => {
    formState.value.activeFieldId = questionnaireStore.activeFieldId
    formState.value.activeFieldIndex = questionnaireStore.activeFieldIndex
    formState.value.formLength = questionnaireStore.formLength
    formState.value.isLastField = questionnaireStore.isLastField
    updateValidationState()
  }

  const updateValidationState = () => {
    const totalErrorCount = Object.values(fieldValidationErrors.value).filter(
      (hasError) => hasError
    ).length

    const currentFieldHasError =
      fieldValidationErrors.value[questionnaireStore.activeFieldId] || false

    const currentField = questionnaireStore.getFieldByUuid(questionnaireStore.activeFieldId)
    const currentFieldValue = questionnaireStore.formData[questionnaireStore.activeFieldId]

    const hasEmptyAsChoice =
      currentField && currentField.choices && currentField.choices.includes('')

    const isRequiredFieldEmpty =
      currentField &&
      !currentField.optional &&
      (currentFieldValue === undefined ||
        currentFieldValue === null ||
        (currentFieldValue === '' && !hasEmptyAsChoice) ||
        (Array.isArray(currentFieldValue) && currentFieldValue.length === 0))

    formState.value.valid = !currentFieldHasError && !isRequiredFieldEmpty
    formState.value.errorLength = totalErrorCount
  }

  watch(fieldValidationErrors, updateValidationState, { deep: true })
  watch(() => questionnaireStore.formData, updateValidationState, { deep: true })
  watch(() => questionnaireStore.activeFieldIndex, updateFormState)
  watch(() => questionnaireStore.formLength, updateFormState)
  watch(() => questionnaireStore.isLastField, updateFormState)

  // Initialize validation state for the initial field
  nextTick(() => {
    updateFormState()
  })

  function validateField(value: boolean) {
    formState.value.valid = value
  }

  function handleFieldValidationError(fieldId: string, hasError: boolean) {
    updateFieldValidationError(fieldId, hasError)
  }

  async function onSubmit() {
    await nextTick()

    if (!formState.value.valid) {
      validateField(false)
    } else {
      validateField(true)

      // Check if we're at the last field of a repeatable section
      if (questionnaireStore.isLastFieldOfRepeatableSection) {
        const sectionInfo = questionnaireStore.currentRepeatableSectionInfo
        if (sectionInfo && questionnaireStore.canAddMoreRepetitions(sectionInfo)) {
          questionnaireStore.setRepeatSectionPrompt(true, sectionInfo)
          return
        }
      }

      questionnaireStore.goToNextField()
      updateFormState()
    }
  }

  // Helper to generate unique section title with incrementing number
  function getUniqueSectionTitle(baseTitle: string, schema: QuestionnaireSection[]): string {
    // Strip trailing " -- number" suffix to get the clean base title
    const cleanBaseTitle = baseTitle.replace(/\s+--\s+\d+$/, '')

    const existingTitles = new Set<string>(schema.map((s) => s.title.toLowerCase()))

    let counter = 1
    let newTitle = `${cleanBaseTitle} -- ${counter}`
    while (existingTitles.has(newTitle.toLowerCase())) {
      counter++
      newTitle = `${cleanBaseTitle} -- ${counter}`
    }

    return newTitle
  }

  // Called when user clicks "Yes, add another" on the repeat prompt
  async function handleAddAnotherSection(submission: any) {
    await nextTick()

    if (!questionnaireStore.pendingRepeatSectionInfo || !submission) {
      handleContinueWithoutRepeat()
      return
    }

    const sectionInfo = questionnaireStore.pendingRepeatSectionInfo
    const originalSection = sectionInfo.section

    // Find the highest tag_number for this participant type
    let maxTagNumber = 0
    for (const section of submission.schema || []) {
      if ((section as any).participant_type === sectionInfo.participantType) {
        for (const item of section.items || []) {
          if (item.tag_number && item.tag_number > maxTagNumber) {
            maxTagNumber = item.tag_number
          }
        }
      }
    }

    // Create a duplicate section with new UUIDs and incremented tag_number
    // Track the original section UUID for counting repetitions
    const originalSectionUuid =
      (originalSection as any).original_section_uuid || originalSection.uuid
    const newSection: QuestionnaireSection = {
      ...JSON.parse(JSON.stringify(originalSection)),
      uuid: uuidv4(),
      title: getUniqueSectionTitle(originalSection.title, submission.schema || []),
      original_section_uuid: originalSectionUuid
    } as QuestionnaireSection

    // Update items with new UUIDs and incremented tag_number
    newSection.items = (originalSection.items || []).map((item: QuestionnaireQuestion) => ({
      ...JSON.parse(JSON.stringify(item)),
      uuid: uuidv4(),
      tag_number: maxTagNumber + 1,
      value: null // Clear previous values
    }))

    // Find the index of the original section and insert the new one after it
    const originalSectionIndex = submission.schema.findIndex(
      (s: QuestionnaireSection) => s.uuid === originalSection.uuid
    )
    const insertIndex = originalSectionIndex + 1

    // Insert the new section
    const updatedSchema = [...submission.schema]
    updatedSchema.splice(insertIndex, 0, newSection)

    try {
      // Save to backend
      await QuestionnaireSubmissionsService.questionnaireSubmissionsPartialUpdate({
        uuid: submission.uuid || '',
        requestBody: {
          schema: updatedSchema,
          closed: false
        }
      })

      // Update local submission
      submission.schema = updatedSchema

      // Reinitialize the form with the new schema
      questionnaireStore.initializeForm(updatedSchema)

      // Jump to the first question of the new section
      const firstQuestionUuid = newSection.items?.[0]?.uuid
      if (firstQuestionUuid) {
        questionnaireStore.jumpToField(firstQuestionUuid)
      }

      updateFormState()
    } catch (error) {
      console.error('Error duplicating section:', error)
      toast({
        title: 'Error',
        description: 'Failed to add another section. Please try again.',
        variant: 'destructive'
      })
    }

    questionnaireStore.setRepeatSectionPrompt(false, null)
  }

  // Called when user clicks "No, continue" on the repeat prompt
  async function handleContinueWithoutRepeat() {
    await nextTick()
    questionnaireStore.setRepeatSectionPrompt(false, null)
    questionnaireStore.goToNextField()
    updateFormState()
  }

  function back() {
    // Store's goToPreviousField also resets the repeat section prompt
    questionnaireStore.goToPreviousField()
    updateFormState()
  }

  function next() {
    questionnaireStore.goToNextField()
    updateFormState()
  }

  function jumpToField(uuid: string) {
    // Store's jumpToField also resets the repeat section prompt
    questionnaireStore.jumpToField(uuid)
    updateFormState()
  }

  function setToLastField() {
    const lastIndex = questionnaireStore.formLength - 1
    if (lastIndex >= 0) {
      questionnaireStore.jumpToField(questionnaireStore.flatFields[lastIndex]?.uuid || '')
      updateFormState()
    }
  }

  function getFieldByUuid(uuid: string) {
    return questionnaireStore.getFieldByUuid(uuid)
  }

  function updateField(uuid: string, value: any) {
    questionnaireStore.updateFormData(uuid, value)
  }

  const getGlobalValidationState = () => {
    const hasAnyErrors = Object.values(fieldValidationErrors.value).some((hasError) => hasError)
    const totalErrorCount = Object.values(fieldValidationErrors.value).filter(
      (hasError) => hasError
    ).length
    const fieldsWithErrors = Object.keys(fieldValidationErrors.value).filter(
      (fieldId) => fieldValidationErrors.value[fieldId]
    )

    return {
      hasAnyErrors,
      totalErrorCount,
      fieldsWithErrors,
      allValidationErrors: fieldValidationErrors.value
    }
  }

  return {
    formState,
    fieldValidationErrors,
    validateField,
    handleFieldValidationError,
    saveFieldData,
    onSubmit,
    back,
    next,
    jumpToField,
    setToLastField,
    getFieldByUuid,
    updateField,
    getGlobalValidationState,
    fields: fieldsWithComponents,
    // Repeat section functionality
    showRepeatSectionPrompt,
    pendingRepeatSectionInfo,
    handleAddAnotherSection,
    handleContinueWithoutRepeat
  }
}

export function useFieldValidation(props: any, emit: any) {
  const fieldValidation = computed<FieldValidation>(() => ({
    tag: props.field.tag,
    tag_relationship: props.field.tag_relationship,
    tag_number: props.field.tag_number,
    type: props.field.type,
    optional: props.field.optional,
    choices: props.field.choices
  }))

  const { validate } = useValidationService(fieldValidation.value)

  const validationError = ref('')
  const isInvalid = ref(false)

  function validateCurrentValue() {
    const result = validate(props.modelValue)
    validationError.value = result.errors[0] || ''
    isInvalid.value = !result.valid
    emit('validation-error', !result.valid)
  }

  watch(
    () => props.field,
    () => {
      validateCurrentValue()
    },
    { deep: true }
  )

  onMounted(() => {
    const isEmptyArray = Array.isArray(props.modelValue) && props.modelValue.length === 0
    if (
      props.modelValue !== undefined &&
      props.modelValue !== null &&
      props.modelValue !== '' &&
      !isEmptyArray
    ) {
      validateCurrentValue()
    }
  })

  function handleInputChange(value: any) {
    emit('update:modelValue', value)

    const result = validate(value)
    validationError.value = result.errors[0] || ''
    isInvalid.value = !result.valid
    emit('validation-error', !result.valid)
  }

  return {
    validationError,
    isInvalid,
    handleInputChange,
    validate
  }
}
